import React, { useState } from 'react';
import { Accordion, Button, Col, OverlayTrigger, Popover, Row, Spinner, Table } from 'react-bootstrap-5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers } from '@fortawesome/pro-regular-svg-icons';
import { formatPhoneNumber } from 'helpers/formatters/phone_number_formats';
import { TENANT_EMPLOYEE_SIZE_MAP, TENANT_SALES_REVENUE_MAP } from '../../constants';
import type { Tenant } from 'bundles/connect/types';
import EmbeddedMode from '../embedded_mode';
import { faBriefcaseArrowRight, faTimes } from '@fortawesome/pro-solid-svg-icons';
import { syncTenantToRethink } from 'bundles/connect/helpers/prospect_api';
import { useSelector } from 'react-redux';
import { selectOffMarketPropertyId } from '../store/selectors/off_market_property';
import { displayToast } from 'bundles/connect/helpers/display_toast';

type Props = {
  tenant: Tenant;
  index: number;
};

export default function PropertyTenantRow({ tenant, index }: Props) {
  const insightsPropertyId = useSelector(selectOffMarketPropertyId);
  const [loading, setLoading] = useState(false);

  const closePopover = () => {
    const button = document.getElementById(`save-tenant-${index}`);
    // eslint-disable-next-line no-unused-expressions
    button?.click();
  };

  const handleSyncTenantData = () => {
    setLoading(true);
    syncTenantToRethink({ insightsPropertyId: insightsPropertyId, tenantIndex: index }).then(() => {
      displayToast('Tenant saved successfully', 'success');
      closePopover();
    }).finally(() => {
      setLoading(false);
    });
  };

  return (
    <div className="bg-light">
      <Row className="px-3 pt-3">
        <Col>
          <div className="fw-bolder"> {tenant.companyName} </div>
          <small className="text-muted d-block">{tenant.streetAddress1} {tenant.streetAddress2}</small>
          <small className="text-muted d-block">{tenant.city}, {tenant.state} {tenant.zip}</small>
        </Col>
        <EmbeddedMode>
          <Col xs="auto">
            <div className="d-flex gap-5">
              <div>
                <div className="fw-bold">
                  {TENANT_EMPLOYEE_SIZE_MAP[tenant.employeeSize] || tenant.employeeSize}
                </div>
                <div className="text-muted">Employees</div>
              </div>
              <div>
                <div className="fw-bold">
                  {TENANT_SALES_REVENUE_MAP[tenant.salesRevenue] || tenant.salesRevenue}
                </div>
                <div className="text-muted">Revenue</div>
              </div>
            </div>
          </Col>
        </EmbeddedMode>
        <EmbeddedMode display forRethink>
          <Col xs="auto">
            <OverlayTrigger
              overlay={
                <Popover>
                  <Popover.Header as="h6" className="border-bottom p-3 text-align-center">
                    <div className="d-flex align-items-center justify-content-between">
                      Save Tenant
                      <Button className="squared" variant="ghost" onClick={closePopover}>
                        <FontAwesomeIcon icon={faTimes} />
                      </Button>
                    </div>
                  </Popover.Header>
                  <Popover.Body className="border-bottom">
                    <div>Adding a tenant will associate this with a property as a space. The first contact will be added as a contact for the tenant.</div>
                    <div className="pt-2">If a property does not exist, a new one will be created with the tenant and the first contact.</div>
                  </Popover.Body>
                  <div className="d-flex p-3 justify-content-end">
                    <Button disabled={loading} variant="secondary" onClick={handleSyncTenantData}>
                      Save Tenant {loading && <Spinner size="sm" />}
                    </Button>
                  </div>
                </Popover>
              }
              placement="top"
              trigger="click"
            >
              <Button id={`save-tenant-${index}`} variant="primary">
                <FontAwesomeIcon className="me-2" icon={faBriefcaseArrowRight} />
                Save Tenant
              </Button>
            </OverlayTrigger>
          </Col>
        </EmbeddedMode>
      </Row>
      <Row className="p-3">
        <Col>
          {tenant.sic1 && (
            <div className="d-flex gap-2">
              <div className="fw-bolder">SIC Code:</div>
              <div className="text-muted">{tenant.sic1} - {tenant.industry1}</div>
            </div>
          )}
          {tenant.website && (
            <div className="d-flex gap-2">
              <div className="fw-bolder">Website:</div>
              <a href={`https://${tenant.website}`} rel="noopener noreferrer" target="_blank">{tenant.website}</a>
            </div>
          )}
        </Col>
      </Row>
      {tenant.contacts.length == 0 && (
        <Row className="p-3">
          <Col>
            <div className="accordion-header-empty">
              <div className="d-flex align-items-center gap-2">
                <FontAwesomeIcon className="text-primary" icon={faUsers} />
                <div className="fw-bolder">No Contacts Found</div>
              </div>
            </div>
          </Col>
        </Row>
      )}
      {tenant.contacts.length > 0 && (
        <Row className="p-3">
          <Col>
            <Accordion>
              <Accordion.Item eventKey="0">
                <Accordion.Header>
                  <div className="d-flex gap-2">
                    <FontAwesomeIcon className="text-primary" icon={faUsers} />
                    <div className="fw-bolder">Contacts ({tenant.contacts.length})</div>
                  </div>
                </Accordion.Header>
                <Accordion.Body className="p-0">
                  <Table className="mb-0" responsive style={{ minWidth: '800px' }}>
                    <thead>
                      <tr>
                        <th style={{ minWidth: '200px' }}>Name</th>
                        <th style={{ minWidth: '200px' }}>Title</th>
                        <th style={{ minWidth: '200px' }}>Emails</th>
                        <th style={{ minWidth: '200px' }}>Phone Numbers</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tenant.contacts.map((contact, idx) => (
                        <tr key={idx}>
                          <td>{contact.contactName}</td>
                          <td>{contact.contactJobTitle}</td>
                          <td>
                            {contact.emails.map(email => (
                              <div key={email}><a href={`mailto:${email}`}>{email}</a></div>
                            ))}
                          </td>
                          <td>
                            {contact.phoneNumbers.map(number => (
                              <div key={number}><a href={`tel:${number}`}>{formatPhoneNumber(number)}</a></div>
                            ))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          </Col>
        </Row>
      )}
    </div>
  );
}
